import React, { useContext, useEffect, useState } from "react";
import { MantineReactTable, MRT_ToggleFullScreenButton, useMantineReactTable } from "mantine-react-table";
import { Divider } from "@mantine/core"
import { IoMdArrowDropdown, IoMdArrowDropright } from "react-icons/io";
import { getHardcodedColor } from "../../utils";
import { css } from '@emotion/css';
import StandalonePathExplorerContext from "../../providers/StandalonePathExplorerProvider";

export interface PathsData {
    data: {
        node_labels: string[];
        rel_types: string[];
        paths: { node_names: string[], rel_names: string[] }[];
        count: number;
    };
    metaId: number
}

const MetaPathTable: React.FC<PathsData> = ({ data, metaId }) => {
    const [collapsed, setCollapsed] = useState(true);
    const { selectedHeader, setSelectedHeader, setBarChartData, setSelectedType } = useContext(StandalonePathExplorerContext)

    let columns = [
        {
            id: "head-expand",
            columnDefType: 'display',
            Header: () => (
                collapsed ?
                    <IoMdArrowDropright fontSize={25} style={{ cursor: "pointer" }} onClick={() => setCollapsed(false)} />
                    : <IoMdArrowDropdown fontSize={25} style={{ cursor: "pointer" }} onClick={() => setCollapsed(true)} />
            ),
            maxSize: 20,
        }
    ]

    const totalColumns = data.node_labels.length + data.rel_types.length;
    for (let i = 0; i < totalColumns; i++) {

        if (i % 2 === 0 && i / 2 < data.node_labels.length) {
            const nodeIndex = Math.floor(i / 2);

            const nodeValues = data.paths.map(path => path.node_names[nodeIndex]);
            const allSame = nodeValues.every(value => value === nodeValues[0]);

            // node column
            columns.push({
                id: `n_${nodeIndex}`,
                accessorFn: (row) => nodeIndex >= row.node_names.length ? '' : row.node_names[nodeIndex],
                header: allSame ? nodeValues[0] : data.node_labels[nodeIndex],
                Header: ({ column }) => (
                    <span
                        onClick={allSame ? undefined : () => {
                            const globalId = `${column.columnDef.id}_${metaId}`
                            if (selectedHeader === globalId) {
                                setSelectedType(null)
                                setSelectedHeader(null)
                                setBarChartData(null)
                            } else {
                                setSelectedType(data.node_labels[nodeIndex])
                                setSelectedHeader(globalId)
                                setBarChartData(
                                    Object.entries(
                                        nodeValues.reduce((acc, v) => ((acc[v] = (acc[v] || 0) + 1), acc), {} as Record<string, number>)
                                    ).map(([label, count]) => ({ label, count }))
                                )
                            }
                        }}
                        style={{ cursor: allSame ? undefined : 'pointer' }}
                    >
                        {column.columnDef.header}
                    </span>
                ),
                enableColumnFilter: !collapsed && !allSame,
                enableColumnActions: false,
                enableSorting: false,
            });
        }

        else if (i % 2 !== 0 && (i - 1) / 2 < data.rel_types.length) {
            const relIndex = Math.floor((i - 1) / 2);
            // edge column
            columns.push({
                id: `r_${relIndex}`,
                accessorFn: (row) => relIndex >= row.rel_names.length ? '' : row.rel_names[relIndex],
                // header: data.rel_types[relIndex],
                Header: () => <Divider label={data.rel_types[relIndex]} labelPosition="center" />,
                Cell: ({ cell }) => !cell.getValue() ? <></> : <Divider label={cell.getValue()} labelPosition="center" />,
                enableColumnActions: false,
                enableColumnFilter: false,
                enableSorting: false
            });
        }
    }

    columns.push({
        id: "count",
        accessorFn: () => '',
        header: data.count,
        Cell: ({ cell }) => <></>,
        enableColumnActions: false,
        enableColumnFilter: false,
        enableSorting: false
    })

    const table = useMantineReactTable({
        columns,
        data: collapsed ? [] : data.paths,
        enableRowSelection: false,
        enableSelectAll: false,
        enableColumnResizing: false,
        enableDensityToggle: false,
        renderTopToolbar: false,
        enableGlobalFilter: true,
        enableHiding: false,
        enableStickyHeader: true,
        enableClickToCopy: false,
        mantineTableBodyProps: {
            style: { display: collapsed ? "none" : "table-row-group" },
        },
        mantineTableBodyRowProps: {
            style: { borderBottom: "none" },
        },
        mantineTableProps: {
            style: {
                borderCollapse: 'separate',
                borderSpacing: '3px',
                tableLayout: "fixed"
            }
        },
        initialState: {
            density: 'xs',
            showColumnFilters: true,
            pagination: {
                pageIndex: 0,
                pageSize: 5
            },
        },
        mantinePaperProps: {
            withBorder: false,
            shadow: 'initial',
            className: css`
              .mantine-Table-td {
                padding: 5px !important;
                padding-top: 3px !important;
                padding-bottom: 3px !important;
              }
            `
        },
        mantineTableHeadCellProps: ({ column }) => {
            const globalId = `${column.id}_${metaId}`
            const isSelected = selectedHeader === globalId
            const isNode = column.id.startsWith('n')
            const nodeIdx = Math.floor(column.getIndex() / 2)
            const label = data.node_labels[nodeIdx]
            const nodeColor = isNode && label ? getHardcodedColor(data.node_labels[nodeIdx]) : undefined
            const isTerminal = label !== column.columnDef.header
            return {
                align: 'center',
                className: css`
                  .mrt-table-head-cell-labels {
                    padding-left: 0px !important;
                    padding-right: 0px !important;
                    padding-top: 0px !important;
                    padding-bottom: 0px !important;
                  }
                `,
                style: {
                    border: isNode && label ? `2px solid ${nodeColor}` : '0px',
                    color: isNode && !isTerminal ? nodeColor : isNode && isTerminal ? '#fff' : '#000',
                    borderRadius: 3,
                    padding: 5,
                    alignContent: 'center',
                    backgroundColor: isNode && isTerminal && label ? `${nodeColor}` : isSelected? '#f8f6d3' : undefined
                },
            }
        },
        mantineTableBodyCellProps: ({ column, cell }) => {
            const isNode = column.id.startsWith('n')
            const nodeIdx = Math.floor(column.getIndex() / 2)
            const nodeColor = isNode ? getHardcodedColor(data.node_labels[nodeIdx]) : undefined;
            return {
                style: {
                    backgroundColor: isNode && cell.getValue() ? `${nodeColor}` : undefined,
                    borderRadius: 3,
                    padding: 0,
                    textAlign: "center",
                    color: isNode ? '#fff' : undefined
                },
            }
        },
        mantineBottomToolbarProps: {
            style: {
                display: collapsed ? 'none' : 'block'
            }
        },
        mantineTopToolbarProps: {
            style: {
                display: collapsed ? 'none' : 'block',
                padding: 0
            }
        },
        renderToolbarInternalActions: ({ table }) => (
            <>
                <MRT_ToggleFullScreenButton table={table} />
            </>
        ),
    })

    return (
        <MantineReactTable table={table} />
    );
}

export default MetaPathTable;
